import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { getMarketingList } from "../../core/api/marketing";
import OfferDetailsView from "../Marketing/offerPreview";

import { Tabs } from "antd";
import OfferPricingList from "./offerPricingSKUs";
import EnvelopeList from "./envelopslist";
import PhoneNumbers from "./phoneNumbers";
import { message } from "antd";
import EditMarketing from "./EditMarketing";
import Validator from "../Validator/main";

import MarketingPriorityList from "./marketingpriority";
import MarketingOffers from "./MarketingOffers";
import MarketingCostForm from "./MarketingCostForm";
import MarketingCostBreakdownList from "./MarketingCostBreakdownList";

const MarketingList = () => {
  const data = useSelector((state) => state.toggle_header);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");

  // Tab navigation setup
  const tabKeyToPath = {
    1: "offers",
    2: "skus",
    3: "edit-marketing",
    4: "envelopes",
    5: "phone-numbers",
    6: "file-validator",
    7: "marketing-priority",
    8: "marketing-cost",
    9: "marketing-cost-breakdown",
  };

  const pathToTabKey = Object.entries(tabKeyToPath).reduce((acc, [key, path]) => {
    acc[path] = key;
    return acc;
  }, {});

  const pathParts = location.pathname.split("/");
  const currentTab = pathParts[2];
  const marketingId = currentTab === "edit-marketing" && pathParts.length > 3 ? pathParts[3] : null;
  const activeTabKey = pathToTabKey[currentTab] || "1";

  const [marketings, setMarketings] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = useRef(null);
  const gridApiRef = useRef(null);
  // Track in-flight request key to avoid duplicate API calls
  const pendingRequestRef = useRef(null);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [offerId, setOfferId] = useState(null);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [clientId, setClientId] = useState(null);
  const sidebarRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [isRightSideBarEditMode, setIsRightSideBarEditMode] = useState(false);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const prevSelectedClientsRef = useRef(null);
  const toggleSidebarEditMode = () => {
    setIsRightSideBarEditMode((prevMode) => !prevMode);
  };
  const handleCloseRightSideBar = () => {
    setSidebarVisible(false);
    setOfferId(null);
    setIsRightSideBarEditMode(false);
    fetchMarketings();
  };
  const fetchMarketings = async (page = 1, pageSize = 25) => {
    // Ensure page and pageSize are valid numbers
    const validPage = typeof page === "number" && !isNaN(page) ? page : 1;
    const validPageSize = typeof pageSize === "number" && !isNaN(pageSize) ? pageSize : 25;

    // Build unique key for this request
    const requestKey = `${selectedClients.join(",")}-${validPage}-${validPageSize}`;
    if (pendingRequestRef.current === requestKey) {
      console.log("Skipping duplicate marketing request", requestKey);
      return;
    }
    pendingRequestRef.current = requestKey;

    // Performance monitoring metrics
    const perfMetrics = {
      requestStartTime: performance.now(),
      responseReceivedTime: 0,
      processingStartTime: 0,
      processingEndTime: 0,
      renderStartTime: 0,
      renderEndTime: 0,
    };

    setLoading(true);
    // Show loading overlay in the grid if it's available
    if (gridApiRef.current) {
      gridApiRef.current.showLoadingOverlay();
    }

    // Declare response variable outside try block so it's accessible in finally block
    let response = null;

    try {
      response = await getMarketingList(selectedClients, validPage, validPageSize);

      // Mark response received time
      perfMetrics.responseReceivedTime = performance.now();

      // Mark processing start time
      perfMetrics.processingStartTime = performance.now();

      const { results, total, total_pages, page: currentPage, page_size } = response.data;

      // Add index to each marketing item
      const indexedResults = results.map((item, idx) => ({
        ...item,
        index: (currentPage - 1) * page_size + idx + 1,
      }));

      // Mark processing end time before state updates
      perfMetrics.processingEndTime = performance.now();

      // Mark render start time (before state updates that trigger renders)
      perfMetrics.renderStartTime = performance.now();

      // Update state (which will trigger renders)
      setMarketings(indexedResults);
      setFilteredMarketings(indexedResults);
      setTotalItems(total);
      setPage(currentPage);
      setPageSize(page_size);
      setTotalPages(total_pages);
      setHasMore(currentPage < total_pages);
      setDataLoaded(true);
      setInitialLoadComplete(true);
    } catch (error) {
      // Show no rows overlay if there's an error
      if (gridApiRef.current) {
        gridApiRef.current.showNoRowsOverlay();
      }
      message.error("Failed to load marketing offers. Please try again.");
    } finally {
      // Mark render end time
      perfMetrics.renderEndTime = performance.now();

      // Clear pending request flag
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }

      setLoading(false);
      setIsLoadingMore(false);
      // Hide loading overlay
      if (gridApiRef.current) {
        gridApiRef.current.hideOverlay();
      }
    }
  };

  useEffect(() => {
    // Convert selectedClients to string for comparison to detect actual changes
    const currentClientsKey = selectedClients ? JSON.stringify([...selectedClients].sort()) : "";
    const previousClientsKey = prevSelectedClientsRef.current
      ? JSON.stringify([...prevSelectedClientsRef.current].sort())
      : "";

    console.log(
      "🔍 useEffect[selectedClients] - Current:",
      currentClientsKey,
      "Previous:",
      previousClientsKey,
      "Loading:",
      loading,
      "InitialLoadComplete:",
      initialLoadComplete
    );

    // Only fetch if:
    // 1. We have valid selectedClients
    // 2. We're not already loading (prevent concurrent requests)
    // 3. Either this is the first load OR the clients actually changed
    const shouldFetch =
      selectedClients &&
      selectedClients.length > 0 &&
      !loading &&
      (!initialLoadComplete || currentClientsKey !== previousClientsKey);

    if (shouldFetch) {
      console.log(
        "📊 useEffect[selectedClients] triggered fetch - Initial load:",
        !initialLoadComplete,
        "Clients changed:",
        currentClientsKey !== previousClientsKey
      );
      prevSelectedClientsRef.current = [...selectedClients];
      fetchMarketings(1, pageSize);
    } else {
      console.log("⏸️ useEffect[selectedClients] skipped fetch - shouldFetch:", shouldFetch);
    }
  }, [selectedClients, loading, initialLoadComplete, pageSize]);

  useEffect(() => {
    const updateSidebarWidth = () => {
      if (sidebarRef.current) {
        setSidebarWidth(sidebarRef.current.getBoundingClientRect().width);
      }
    };

    if (isSidebarVisible) {
      updateSidebarWidth();
      window.addEventListener("resize", updateSidebarWidth);
    }

    return () => {
      window.removeEventListener("resize", updateSidebarWidth);
    };
  }, [isSidebarVisible]);

  const uniqueValues = (data, field) => {
    if (!Array.isArray(data)) return [];
    if (!Array.isArray(data)) return [];

    return [...new Set(data.map((item) => field.split(".").reduce((obj, key) => obj?.[key], item)))]
      .filter(Boolean)
      .map((value) => ({ text: value, value }));
  };

  const [printId, setPrintId] = useState(null);

  const handleAddNewMarketing = async () => {
    if (selectedClients.length === 0 || selectedClients.length > 1) {
      message.warning("Please select one client.");
      return;
    }
    setIsRightSideBarEditMode(true);
    setOfferId(null);
    setClientId(selectedClients[0]);
    navigate(`/marketing/${tabKeyToPath[3]}`);
  };

  const [filteredMarketings, setFilteredMarketings] = useState([]);
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);

    if (value) {
      const filtered = marketings.filter(
        (item) =>
          (item.brand_name && item.brand_name.toLowerCase().includes(value)) ||
          (item.code_offer && item.code_offer.toLowerCase().includes(value)) ||
          (item.product_variations_detail &&
            item.product_variations_detail.some(
              (pv) => pv.description && pv.description.toLowerCase().includes(value)
            ))
      );
      setFilteredMarketings(filtered);
    } else {
      setFilteredMarketings(marketings);
    }
  };

  const [isEditMode, setIsEditMode] = useState(false);

  return (
    <div className="page-wrapper">
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => navigate(`/marketing/${tabKeyToPath[key]}`)}
        className="p-2"
        size="small"
        type="card"
        tabBarStyle={{ marginBottom: "5px" }}
        items={[
          {
            key: "1",
            label: "Marketing Offers",
            children: (
              <MarketingOffers
                data={data}
                tabKeyToPath={tabKeyToPath}
                handleAddNewMarketing={handleAddNewMarketing}
                setClientId={setClientId}
                setIsRightSideBarEditMode={setIsRightSideBarEditMode}
                setSidebarVisible={setSidebarVisible}
                setOfferId={setOfferId}
                setPrintId={setPrintId}
                selectedClients={selectedClients}
                searchQuery={searchQuery}
                handleSearch={handleSearch}
                marketings={marketings}
                setPage={setPage}
                setPageSize={setPageSize}
                fetchMarketings={fetchMarketings}
                loading={loading}
                dataLoaded={dataLoaded}
                filteredMarketings={filteredMarketings}
                tableContainerRef={tableContainerRef}
                gridApiRef={gridApiRef}
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                totalPages={totalPages}
                setToogleHeader={setToogleHeader}
              />
            ),
          },
          {
            key: "2",
            label: "Marketing SKUs",
            children: <OfferPricingList />,
          },
          {
            key: "3",
            label: marketingId ? "Edit Marketing" : "Create Marketing",
            children: <EditMarketing id={offerId} isEditMode={isEditMode} cId={clientId} />,
          },
          {
            key: "4",
            label: "Envelopes",
            children: <EnvelopeList />,
          },
          {
            key: "5",
            label: "Phone Numbers",
            children: <PhoneNumbers />,
          },
          {
            key: "6",
            label: "Marketing File Validator",
            children: <Validator />,
          },
          {
            key: "7",
            label: "Marketing Priority",
            children: <MarketingPriorityList />,
          },
          {
            key: "8",
            label: "Marketing Costs",
            children: <MarketingCostForm clientId={clientId} />,
          },
        ]}
      />
      <OfferDetailsView
        isVisible={isSidebarVisible}
        onClose={handleCloseRightSideBar}
        offerId={offerId}
        clientId={clientId}
        ref={sidebarRef}
        editMode={isRightSideBarEditMode}
        toggleEdit={toggleSidebarEditMode}
        onResize={setSidebarWidth}
        printId={printId}
      />
    </div>
  );
};

export default MarketingList;
